import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetAttendanceSessionStudentsQuery,
  useGetStudentAttendanceHistoryQuery,
  useLateArrivalAttendanceSessionMutation,
  useSaveAttendanceSessionNoteMutation,
  useSendClassSessionMessageMutation,
  useStartAttendanceSessionMutation,
  useSubmitAttendanceSessionMutation,
  useGetAttendanceMessageTemplatesQuery,
} from "../../Api/attoDeskApi";
import { API_URL } from "../../config";
import { getAvatar } from "../../utils/avatar";
import { presentDateOnly } from "../../utils/dateOnly";
import { formatLateDuration } from "../../utils/lateDuration";
import { formatClassLabel } from "../../utils/classLabel";
import SendMessagePanel, {
  MessageRecipients,
} from "../../Components/SendMessagePanel";
import { useConfirm } from "../../Components/Confirm";
import GradeSubjectsDialog, { summarizeGradesCompact } from "./GradeSubjectsDialog";
import { Tooltip } from "@mui/material";
import useUser from "../../Core/hook/useUser";

// ─────────────── types ─────────────── //
type StatusValue = "present" | "absent" | "late" | "not_marked";

interface StudentRow {
  student_id: number;
  name: string;
  avatar: string | null;
  status: StatusValue;
  arrived_at?: string | null;
  late_reason?: string | null;
}
interface SubjectGroup {
  subject_id: number | null;
  subject_name: string;
  students: StudentRow[];
}
// Preferred grouping — one card per real (grade, subject) pair on the class.
// Backend `groups` field. When absent (legacy response), the FE synthesises
// pseudo-groups from `subjects` for back-compat.
interface PairGroup {
  pair_id: number;
  grade_id: number;
  grade_name: string;
  grade_sort_order?: number | null;
  subject_id: number | null;
  subject_name: string;
  subject_sort_order?: number | null;
  students: StudentRow[];
}

// Row-level extension: when the same (student_id, subject_id) has already
// been rendered in an earlier pair-group, subsequent appearances are
// read-only mirrors of the canonical row. The DB's unique index is
// (occurrence_id, student_id, subject_id) — one attendance record per
// (student, subject) — so three "Grade 2/3/4 English" cards for the same
// student all point at ONE row. The frontend state key is
// `${student}:${subject}`; without this dedupe every click flipped every
// visible copy.
interface StudentRowDedup extends StudentRow {
  _dupeOfHeader?: string; // first group header this student:subject appeared in
}
interface GradeSubjectsBucket {
  grade_id: number;
  grade: { id: number; name: string; sort_order?: number | null };
  subjects: Array<{ id: number; name: string; sort_order?: number | null }>;
}
interface SessionData {
  occurrence_id: number;
  class_id: number;
  grade: string | null;
  staff_name: string | null;
  location: string | null;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start_at?: string | null;
  actual_end_at?: string | null;
  // 'manual' (teacher ended via End Class), 'system' (auto-completed
  // by cron OR by the browser countdown firing at scheduled end),
  // null while still running.
  ended_by?: "manual" | "system" | null;
  late_minutes?: number | null;
  server_time: string;
  subjects: SubjectGroup[];
  groups?: PairGroup[];
  grade_subjects?: GradeSubjectsBucket[];
}

// ─────────────── helpers ─────────────── //
const buildKey = (sid: number, subId: number | null) => `${sid}:${subId ?? 0}`;

const pad = (n: number) => String(n).padStart(2, "0");
const formatElapsed = (ms: number) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};
const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// HH:MM:SS countdown for the running-timer big number. Always three
// segments so the label never shifts width tick-to-tick (paired with
// fontVariantNumeric:"tabular-nums" in the card render). Clamps at
// 00:00:00 so a race between the client and the cron never renders
// a negative value.
const formatCountdownHms = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// Compact human countdown for the pre-class hero card. Buckets:
//   ≥ 24 h  → "1d 03h 22m"
//   ≥ 1 h   → "03h 18m"
//   ≥ 1 min → "42m 16s"
//   < 1 min → "00m 38s"
// Zero-padded so the width stays stable each tick (paired with
// tabular-nums at the render site) — no layout jitter as digits roll.
const formatCountdownCompact = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days >= 1) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  if (hours >= 1) return `${pad(hours)}h ${pad(minutes)}m`;
  return `${pad(minutes)}m ${pad(seconds)}s`;
};

// Late minutes = max(0, arrived - scheduledStart), floored to whole minutes.
const computeLateMinutes = (
  scheduledStartMs: number,
  arrivedAtIso: string | null | undefined
): number => {
  if (!arrivedAtIso || !scheduledStartMs) return 0;
  const arrived = new Date(arrivedAtIso).getTime();
  if (isNaN(arrived)) return 0;
  return Math.max(0, Math.floor((arrived - scheduledStartMs) / 60000));
};

// Severity buckets for the "N mins late" pill.
const latePillColor = (mins: number): { bg: string; text: string; border: string } => {
  if (mins <= 10) return { bg: "#fef3c7", text: "#d97706", border: "#fde68a" }; // amber
  if (mins <= 30) return { bg: "#fed7aa", text: "#ea580c", border: "#fdba74" }; // orange
  return { bg: "#fee2e2", text: "#dc2626", border: "#fecaca" };                  // red
};

// ─────────────── PremiumTimePicker (used by Late Check-In modal) ─────────────── //
const PremiumTimePicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value, onChange,
}) => {
  const parts = (value || "00:00").split(":").map((n) => parseInt(n, 10) || 0);
  const h24 = parts[0];
  const [hour, setHour] = useState<number>(h24 % 12 === 0 ? 12 : h24 % 12);
  const [minute, setMinute] = useState<number>(parts[1]);
  const [period, setPeriod] = useState<"AM" | "PM">(h24 >= 12 ? "PM" : "AM");
  const push = (h: number, m: number, p: "AM" | "PM") => {
    let out = h % 12;
    if (p === "PM") out += 12;
    onChange(`${pad(out)}:${pad(m)}`);
  };
  const stepHour = (d: "up" | "down") => {
    const h = d === "up" ? (hour % 12) + 1 : hour === 1 ? 12 : hour - 1;
    setHour(h); push(h, minute, period);
  };
  const stepMin = (d: "up" | "down") => {
    const m = d === "up" ? (minute + 1) % 60 : minute === 0 ? 59 : minute - 1;
    setMinute(m); push(hour, m, period);
  };
  const arrow: CSSProperties = { width: 36, height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", color: "#1b3a6b", fontWeight: 700 };
  const boxInput: CSSProperties = {
    width: 72, height: 72, textAlign: "center",
    fontSize: 36, fontWeight: 700, color: "#1b3a6b",
    background: "#fff", border: "2px solid #e2e8f0", borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", letterSpacing: 2,
    outline: "none", padding: 0, MozAppearance: "textfield" as any,
  };
  const per = (active: boolean): CSSProperties => ({ width: 48, height: 36, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5, background: active ? "#1b3a6b" : "#f1f5f9", color: active ? "#fff" : "#94a3b8" });
  const col: CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", gap: 8 };

  const setHourClamped = (raw: string) => {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    const v = Math.max(1, Math.min(12, n));
    setHour(v);
    push(v, minute, period);
  };
  const setMinuteClamped = (raw: string) => {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return;
    const v = Math.max(0, Math.min(59, n));
    setMinute(v);
    push(hour, v, period);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#f8fafc", border: "2px solid #e2e8f0", borderRadius: 16, padding: "20px 24px" }}>
      {/* Hide browser number-input spinners just for these two inputs. */}
      <style>{`.premium-time-input::-webkit-inner-spin-button, .premium-time-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }`}</style>
      <div style={col}>
        <button style={arrow} onClick={() => stepHour("up")}>▲</button>
        <input
          className="premium-time-input"
          type="number"
          min={1}
          max={12}
          value={pad(hour)}
          onChange={(e) => setHourClamped(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          style={boxInput}
        />
        <button style={arrow} onClick={() => stepHour("down")}>▼</button>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: "#1b3a6b", marginBottom: 4, lineHeight: 1 }}>:</div>
      <div style={col}>
        <button style={arrow} onClick={() => stepMin("up")}>▲</button>
        <input
          className="premium-time-input"
          type="number"
          min={0}
          max={59}
          value={pad(minute)}
          onChange={(e) => setMinuteClamped(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          style={boxInput}
        />
        <button style={arrow} onClick={() => stepMin("down")}>▼</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 8 }}>
        <button style={per(period === "AM")} onClick={() => { setPeriod("AM"); push(hour, minute, "AM"); }}>AM</button>
        <button style={per(period === "PM")} onClick={() => { setPeriod("PM"); push(hour, minute, "PM"); }}>PM</button>
      </div>
    </div>
  );
};

// ─────────────── main page ─────────────── //
const AttendanceSession: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const occurrenceId = Number(id);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { isStaffRole } = useUser();

  const { data, isLoading, isFetching, error, refetch } =
    useGetAttendanceSessionStudentsQuery(occurrenceId, { skip: !occurrenceId });
  const [startSession, startState] = useStartAttendanceSessionMutation();
  const [submitSession, submitState] = useSubmitAttendanceSessionMutation();
  const [saveLateArrival, lateArrivalState] = useLateArrivalAttendanceSessionMutation();
  const [saveNote, saveNoteState] = useSaveAttendanceSessionNoteMutation();
  const [sendMessage, sendMessageState] = useSendClassSessionMessageMutation();

  const session: SessionData | null = (data?.data as SessionData) ?? null;

  // Back / Cancel destination priority:
  //   1. explicit `?return_url=...` from the entry point (admin flows
  //      already pass their class-schedule detail URL);
  //   2. Staff Portal users default to `/attendance` — their top-level
  //      attendance workspace;
  //   3. otherwise fall back to the class-schedule detail page for the
  //      SESSION'S class id (never the occurrence id). Previously the
  //      fallback interpolated `occurrenceId` into `/class-schedules/:classId`,
  //      which is a different id family and produced "Class not found".
  //   4. if the session hasn't loaded yet, land on the classes list
  //      rather than emit a URL with an unknown id.
  const explicitReturn = sp.get("return_url");
  const returnUrl = explicitReturn
    ? explicitReturn
    : isStaffRole
    ? "/attendance"
    : (session?.class_id ? `/class-schedules/${session.class_id}` : "/class-schedules");
  const backLabel = isStaffRole ? "Back to Attendance" : "Back to Class Sessions";

  const [marks, setMarks] = useState<Record<string, StatusValue>>({});
  const [now, setNow] = useState<number>(Date.now());
  const [started, setStarted] = useState<boolean>(false);
  // A completed session is read-only end-to-end: the canonical state is
  // `session.status === 'completed'` OR `session.actual_end_at` non-null
  // (both are written together in submit(); we accept either so a legacy
  // row with only one is still locked). Every mutation control below —
  // row toggle, All Present / All Absent, Late Slip / Change, notes, and
  // Submit — is gated on this flag. The backend enforces the same rule
  // on submit / late-arrival / saveNote so a direct API bypass is
  // rejected with a 422; the UI lock is the polite path.
  const completed = React.useMemo<boolean>(() => {
    if (!session) return false;
    const status = String((session as any).status ?? "").toLowerCase().trim();
    return status === "completed" || !!session.actual_end_at;
  }, [session]);
  const [lateBanner, setLateBanner] = useState<{ lateMinutes: number } | null>(null);
  const [dismissedPrompts, setDismissedPrompts] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  // Grades & Subjects modal — opens from the compact summary shown
  // in both the dark hero and the white Class Information card. The
  // full grade-by-grade breakdown lives ONLY inside this dialog now;
  // the two card surfaces show a one-liner + button so the operational
  // attendance controls stay above the fold.
  const [subjectsModalOpen, setSubjectsModalOpen] = useState<boolean>(false);

  const [lateModal, setLateModal] = useState<{
    studentId: number; subjectId: number | null; studentName: string; subjectName: string;
    arrivedAt: string; reason: string;
  } | null>(null);

  // Row-level UX state: inline notes, email modal, history panel.
  const [noteOpen, setNoteOpen] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [emailModal, setEmailModal] = useState<
    | { studentId: number; name: string; subject: string; body: string }
    | null
  >(null);
  const [historyStudent, setHistoryStudent] = useState<
    { id: number; name: string } | null
  >(null);

  const [showMessagePanel, setShowMessagePanel] = useState(false);
  const [msgPrefill, setMsgPrefill] = useState<
    | { open?: boolean; recipients?: MessageRecipients; studentIds?: number[]; template?: "absent" | "behaviour" | "homework" | "progress" | "late" | "custom"; version?: number }
    | undefined
  >(undefined);

  // Hydrate marks from server payload.
  //
  // Batch 16 — REVERSED the "new default flow". The prior version silently
  // hydrated every not_marked student to `present`, and handleSubmit then
  // wrote those "present" values to the DB — meaning any Start → Submit
  // without touching anyone recorded every enrolled student as present.
  // That contaminated attendance rates, dashboards, and the parent portal.
  //
  // Now: not_marked stays not_marked. The teacher must explicitly mark a
  // student (row-tap, "All Present", "All Absent", or Late Check-In).
  // handleSubmit already skips not_marked records, so an untouched
  // roster writes nothing.
  useEffect(() => {
    if (!session) return;
    const next: Record<string, StatusValue> = {};
    session.subjects.forEach((g) =>
      g.students.forEach((s) => {
        next[buildKey(s.student_id, g.subject_id)] = s.status;
      })
    );
    setMarks(next);
    setStarted(!!session.actual_start_at);
    // eslint-disable-next-line no-console
    console.info("[ATTN_START] session_effect", {
      actual_start_at: session.actual_start_at,
      started_computed: !!session.actual_start_at,
      scheduled_start: session.scheduled_start,
      occurrence_id: session.occurrence_id,
    });
  }, [session]);

  // Ticking clock
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const scheduledStartMs = session ? new Date(session.scheduled_start).getTime() : 0;
  const scheduledEndMs = session ? new Date(session.scheduled_end).getTime() : 0;
  const actualStartMs = session?.actual_start_at ? new Date(session.actual_start_at).getTime() : 0;

  // Server-aligned clock. Captured ONCE per session fetch as the
  // delta between `session.server_time` and the browser wall clock
  // at that moment. Every countdown / auto-end / colour decision
  // reads `alignedNow` instead of raw `Date.now()`, so a user's
  // wrong system clock cannot end the class early or leave it
  // hanging past its scheduled end.
  const serverOffsetRef = React.useRef<number>(0);
  const serverOffsetSessionIdRef = React.useRef<number | null>(null);
  useEffect(() => {
    if (!session?.server_time || !session?.occurrence_id) return;
    if (serverOffsetSessionIdRef.current === session.occurrence_id) return;
    const serverAt = new Date(session.server_time).getTime();
    if (!Number.isFinite(serverAt)) return;
    serverOffsetRef.current = serverAt - Date.now();
    serverOffsetSessionIdRef.current = session.occurrence_id;
  }, [session?.server_time, session?.occurrence_id]);
  const alignedNow = now + serverOffsetRef.current;

  // Session-level status. Buckets:
  //   upcoming     — scheduled in the future
  //   on_time      — started ≤ 10 min after scheduled start
  //   late         — 10 min to 24h after scheduled start
  //   session_done — >24h after scheduled start (fill in past attendance)
  // If the teacher already recorded actual_start_at we lock late-minutes to
  // the actual-vs-scheduled delta (never grows over time).
  const referenceMs = session?.actual_start_at ? actualStartMs : alignedNow;
  const diffMins = session
    ? Math.floor((referenceMs - scheduledStartMs) / 60000)
    : 0;
  // `finished_not_started` fires the instant the scheduled end passes on a
  // session that was never started. It supersedes `late` so the Start button
  // disappears at the end line instead of continuing to count up.
  const finishedNotStarted = !!session && !session.actual_start_at && scheduledEndMs > 0 && alignedNow >= scheduledEndMs;
  const sessionStatus: "upcoming" | "on_time" | "late" | "session_done" | "finished_not_started" =
    !session
      ? "upcoming"
      : finishedNotStarted
      ? "finished_not_started"
      : diffMins <= 0
      ? "upcoming"
      : diffMins / 60 > 24
      ? "session_done"
      : diffMins < 10
      ? "on_time"
      : "late";
  const currentLateMinutes = sessionStatus === "late" ? diffMins : 0;
  // Preserve the old `isLate` boolean used by other code paths.
  const isLate = sessionStatus === "late";
  // Running timer freezes at scheduled end: elapsed = min(now, end) − start.
  const effectiveNowForElapsed = scheduledEndMs > 0 ? Math.min(alignedNow, scheduledEndMs) : alignedNow;
  const remainingMs = scheduledEndMs - alignedNow;
  // Colour urgency (display-only, matches spec §17):
  //   > 30 min : mint      (relaxed)
  //   ≤ 30 min : amber     (approaching)
  //   ≤ 5 min  : red/orange (ending soon)
  //   ≤ 0      : red       (auto-end firing this tick)
  const timerColor = remainingMs <= 0 ? "#fca5a5"
                    : remainingMs <= 5 * 60 * 1000 ? "#fca5a5"
                    : remainingMs <= 30 * 60 * 1000 ? "#fbbf24"
                    : "#86efac";
  const endingSoon = remainingMs > 0 && remainingMs <= 5 * 60 * 1000;

  // ── Pre-start gate (Batch 11) ───────────────────────────────────────
  // Teachers can start up to GRACE_MINUTES before the scheduled start.
  // Before that the button is disabled and we show a live countdown.
  // Backend also rejects out-of-window starts (POST returns 422) — this
  // is the visible half of the same rule.
  const GRACE_MINUTES = 15;
  const graceOpensMs = scheduledStartMs - GRACE_MINUTES * 60000;
  const scheduledStartDate = session ? new Date(scheduledStartMs) : null;
  const beforeGrace = !!session && !session.actual_start_at && alignedNow < graceOpensMs;
  const insideGrace = !!session && !session.actual_start_at && alignedNow >= graceOpensMs && alignedNow < scheduledStartMs;
  const secondsToStart = Math.max(0, Math.floor((scheduledStartMs - alignedNow) / 1000));
  // Batch 14 — magnitude-aware countdown. Above 1 hour we don't show
  // seconds (the second-by-second tick still fires but doesn't reach
  // the label), so a class 4 hours out renders "3 hours 59 minutes"
  // once per minute in practice. Below 1 minute we finally show
  // seconds for the last-second suspense before the grace window opens.
  const pluralize = (n: number, singular: string): string =>
    `${n} ${singular}${n === 1 ? "" : "s"}`;
  const formatCountdown = (secs: number): string => {
    const days = Math.floor(secs / 86400);
    const hours = Math.floor((secs % 86400) / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    if (days >= 1) return `${pluralize(days, "day")} ${pluralize(hours, "hour")}`;
    if (hours >= 1) return `${pluralize(hours, "hour")} ${pluralize(minutes, "minute")}`;
    if (minutes >= 1) return pluralize(minutes, "minute");
    return pluralize(seconds, "second");
  };
  // "Fri Jul 27 · 8:00 PM – 10:00 PM · 2 hours"
  const scheduledWindowLabel = scheduledStartDate && session
    ? (() => {
        const dateStr = scheduledStartDate.toLocaleDateString(undefined, {
          weekday: "short", month: "short", day: "numeric",
        });
        const durationMinutes = Math.max(0, Math.round((scheduledEndMs - scheduledStartMs) / 60000));
        const durationLabel = durationMinutes >= 60
          ? (durationMinutes % 60 === 0
              ? pluralize(Math.floor(durationMinutes / 60), "hour")
              : `${pluralize(Math.floor(durationMinutes / 60), "hour")} ${pluralize(durationMinutes % 60, "minute")}`)
          : pluralize(durationMinutes, "minute");
        return `${dateStr} · ${formatTime(session.scheduled_start)} – ${formatTime(session.scheduled_end)} · ${durationLabel}`;
      })()
    : "";

  // ── Auto-end at scheduled end while the page is open ───────────────
  // Fires the SAME `submit(end_class=true)` mutation the manual End
  // Class button uses, tagged with `end_source: 'auto'` so the BE
  // stamps `ended_by='system'` (parity with AutoCompleteClassSessions
  // cron). Guarded by `autoEndFiredRef` so multi-tab / re-render can't
  // fire twice. Reliability fallback lives on the BE (the cron runs
  // every minute) — this hook is only the "page is open" fast path.
  const autoEndFiredRef = React.useRef(false);
  const endReachedRef = React.useRef(false);
  useEffect(() => {
    if (!session || !scheduledEndMs || !occurrenceId) return;
    if (session.actual_end_at || session.ended_by) {
      endReachedRef.current = true;
      autoEndFiredRef.current = true;
      return;
    }
    if (alignedNow < scheduledEndMs) return;
    if (endReachedRef.current) return;
    endReachedRef.current = true;
    if (session.actual_start_at && !autoEndFiredRef.current) {
      autoEndFiredRef.current = true;
      (async () => {
        try {
          await submitSession({ occurrenceId, records: [], end_class: true, end_source: "auto" }).unwrap();
        } catch {
          // BE idempotency + cron fallback both cover the failure path;
          // a stale/racing 4xx here is not something to surface to the
          // teacher — a refetch below will pull the authoritative state.
        } finally {
          refetch();
        }
      })();
    } else {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alignedNow, scheduledEndMs, session?.actual_end_at, session?.ended_by, session?.actual_start_at, occurrenceId]);

  // Normalised card list — one entry per (grade, subject) pair when the
  // backend emits the new `groups` field, else falls back to the legacy
  // `subjects` shape (grade unavailable per group). Downstream code
  // (stats, render, markAll, allStudentsFlat) iterates THIS instead of
  // `session.subjects` so the old subject-only grouping never leaks
  // through when the backend is on the new payload.
  const displayGroups: PairGroup[] = useMemo(() => {
    const raw: PairGroup[] =
      session?.groups && Array.isArray(session.groups) && session.groups.length > 0
        ? session.groups
        // Back-compat: synthesise pair groups from the legacy subjects shape
        // (grade_id/name/sort_order left null). Card header will fall back
        // to just the subject name when grade is unknown.
        : (session?.subjects || []).map((s, i) => ({
            pair_id: -i - 1,
            grade_id: 0,
            grade_name: session?.grade || "",
            grade_sort_order: null,
            subject_id: s.subject_id,
            subject_name: s.subject_name,
            subject_sort_order: null,
            students: s.students,
          }));
    // Drop grade/subject pairs that carry no students. The class row
    // stores every possible (grade, subject) pair the class *could*
    // cover via `assign_classes.select_grade_subject_ids`, so a class
    // configured with "3 Grades · 9 Subjects" will emit 27 pair
    // groups even if only 1 or 2 are actually populated for this
    // occurrence. Rendering the empty ones produced the "No students
    // in this subject." wall that made the page unusably long.
    //
    // When EVERY group is empty (a genuinely unrostered class) we
    // return an empty list and the renderer shows ONE class-level
    // empty state instead of dumping the whole grade/subject
    // catalogue. Never re-render the wall.
    const nonEmpty = raw.filter((g) => Array.isArray(g.students) && g.students.length > 0);

    // Second pass: annotate `_dupeOfHeader` on any (student, subject) that
    // has already appeared in an earlier group. Attendance is one row per
    // (student, subject) in the DB — mirroring the same student:subject
    // pair as an interactive control across multiple grade cards would
    // let one tap flip every visible copy (since they share state key).
    // Duplicates render as compact read-only mirrors instead.
    const firstHeaderByKey = new Map<string, string>();
    return nonEmpty.map((g) => {
      const header = g.grade_id && g.grade_name
        ? `Grade ${g.grade_name} — ${g.subject_name}`
        : g.subject_name;
      const students = (g.students as StudentRowDedup[]).map((s) => {
        const k = `${s.student_id}:${g.subject_id ?? 0}`;
        const primaryHeader = firstHeaderByKey.get(k);
        if (primaryHeader) {
          return { ...s, _dupeOfHeader: primaryHeader };
        }
        firstHeaderByKey.set(k, header);
        return s;
      });
      return { ...g, students };
    });
  }, [session]);

  const stats = useMemo(() => {
    // KPI totals are STUDENT-UNIQUE, not row-unique. A student in N
    // groups counts as ONE student. Status per (student, subject) is
    // still tracked individually (marks keyed by student:subject), but
    // the aggregate rolls up per-student — worst-of state resolution:
    // absent > late > unmarked > present, so a student marked absent in
    // one section counts absent overall.
    const perStudent = new Map<number, StatusValue>();
    const lateArrivalByStudent = new Map<number, string>();
    displayGroups.forEach((g) =>
      g.students.forEach((s) => {
        const v: StatusValue = marks[buildKey(s.student_id, g.subject_id)] || "not_marked";
        const prev = perStudent.get(s.student_id);
        const rank = (x: StatusValue) => (x === "absent" ? 4 : x === "late" ? 3 : x === "not_marked" ? 2 : 1);
        if (prev === undefined || rank(v) > rank(prev)) perStudent.set(s.student_id, v);
        if (v === "late" && s.arrived_at && !lateArrivalByStudent.has(s.student_id)) {
          lateArrivalByStudent.set(s.student_id, s.arrived_at);
        }
      })
    );
    let present = 0, absent = 0, late = 0, lateMinutes = 0;
    perStudent.forEach((v, sid) => {
      if (v === "present") present += 1;
      else if (v === "absent") absent += 1;
      else if (v === "late") {
        late += 1;
        const arr = lateArrivalByStudent.get(sid);
        if (arr) lateMinutes += Math.max(0, Math.floor((new Date(arr).getTime() - scheduledStartMs) / 60000));
      }
    });
    const total = perStudent.size;
    const unmarked = Math.max(0, total - present - absent - late);
    return { total, unmarked, present, absent, late, lateMinutes };
  }, [displayGroups, marks, scheduledStartMs]);

  const markedCount = stats.present + stats.absent + stats.late;
  // Enable Submit once ANY student has been marked. An untouched roster
  // has nothing to submit (handleSubmit skips not_marked records).
  const allMarked = markedCount > 0;

  const allStudentsFlat = useMemo(() => {
    const out: { id: number; name: string }[] = [];
    const seen = new Set<number>();
    displayGroups.forEach((g) =>
      g.students.forEach((s) => {
        if (!seen.has(s.student_id)) { seen.add(s.student_id); out.push({ id: s.student_id, name: s.name }); }
      })
    );
    return out;
  }, [displayGroups]);

  const setStatus = (studentId: number, subjectId: number | null, status: StatusValue) => {
    const key = buildKey(studentId, subjectId);
    setMarks((m) => ({ ...m, [key]: status }));
  };
  const markAllInGroup = (group: { subject_id: number | null; students: StudentRow[] }, status: "present" | "absent") => {
    // Guard the handler itself, not just the button, so a completed
    // session cannot be mutated via a stale reference or a re-render race.
    if (completed) return;
    setMarks((m) => {
      const next = { ...m };
      group.students.forEach((s) => {
        // Do NOT skip `_dupeOfHeader` rows here. A mirror row is not a
        // separate DB record but it points at the same canonical state
        // key `${student}:${subject}` as its primary. If a group only
        // contains mirrors (e.g. Grade 3 English is a mirror of Grade 2
        // English's canonical row), skipping mirrors would make the
        // group's "All Present / All Absent" button a no-op. Writing
        // the same key twice with the same value is a harmless
        // idempotent set — the marks map dedupes by key.
        const key = buildKey(s.student_id, group.subject_id);
        // Never overwrite a Late Check-In — those are locked to `late`
        // until the teacher explicitly changes them.
        if (next[key] !== "late") next[key] = status;
      });
      return next;
    });
  };
  const markAllPresent = (group: { subject_id: number | null; students: StudentRow[] }) => markAllInGroup(group, "present");

  const resetAllPresent = () => {
    if (!session) return;
    setMarks((m) => {
      const next = { ...m };
      session.subjects.forEach((g) =>
        g.students.forEach((s) => {
          const key = buildKey(s.student_id, g.subject_id);
          if (next[key] !== "late") next[key] = "present";
        })
      );
      return next;
    });
  };

  // Row-click toggle: present ↔ absent (late is not toggled by tap).
  // Batch 16 — inert until the teacher has clicked Start. The roster is
  // visible in the grace window so a teacher can see who's expected,
  // but marking is blocked so an accidental tap can't write anything
  // (Submit is also disabled, and the backend rejects for good measure).
  const toggleRow = (studentId: number, subjectId: number | null) => {
    if (!started) return;
    if (completed) return;
    const key = buildKey(studentId, subjectId);
    const cur = marks[key];
    if (cur === "late") return;
    setStatus(studentId, subjectId, cur === "absent" ? "present" : "absent");
  };
  const handleStart = async () => {
    // eslint-disable-next-line no-console
    console.info("[ATTN_START] click", { occurrenceId, ts: new Date().toISOString() });
    try {
      const res: any = await startSession(occurrenceId).unwrap();
      // eslint-disable-next-line no-console
      console.info("[ATTN_START] mutation_success", {
        status: res?.status,
        message: res?.message,
        actual_start_at: res?.data?.actual_start_at,
        is_late: res?.data?.is_late,
        late_minutes: res?.data?.late_minutes,
        raw: res,
      });
      setStarted(true);
      if (res?.data?.is_late) setLateBanner({ lateMinutes: res.data.late_minutes ?? 0 });
      const refetched: any = await refetch();
      // eslint-disable-next-line no-console
      console.info("[ATTN_START] refetch_complete", {
        actual_start_at: refetched?.data?.data?.actual_start_at,
        status: refetched?.status,
        is_error: refetched?.isError,
      });
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("[ATTN_START] mutation_error", {
        status: e?.status,
        data: e?.data,
        message: e?.data?.message || e?.message,
      });
      setToast({ msg: e?.data?.message || "Failed to start session.", severity: "error" });
    }
  };
  const handleSubmit = async () => {
    if (!session) return;
    if (completed) return;
    const records: any[] = [];
    session.subjects.forEach((g) =>
      g.students.forEach((s) => {
        const status = marks[buildKey(s.student_id, g.subject_id)];
        if (!status || status === "not_marked") return;
        records.push({ student_id: s.student_id, subject_id: g.subject_id, status });
      })
    );
    try {
      await submitSession({ occurrenceId, records, end_class: false }).unwrap();
      setToast({
        msg: "✅ Attendance submitted! Redirecting to attendance list…",
        severity: "success",
      });
      // Always land on the attendance hub after submit, regardless of
      // return_url. The back button above still honors return_url.
      setTimeout(() => navigate("/attendance"), 2000);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to submit attendance.", severity: "error" });
    }
  };

  // Batch 16 — End Class action. Submits whatever's been marked so far
  // AND ends the session server-side (ended_by='manual', status='completed').
  // If nothing has been marked, empty records + end_class=true is now
  // accepted by the backend (see AttendanceSessionController::submit) so
  // a teacher can honestly close a session without fabricating attendance.
  const confirm = useConfirm();
  const handleEndClass = async () => {
    if (!session) return;
    const marked = stats.present + stats.absent + stats.late;
    if (marked < stats.total) {
      const remaining = stats.total - marked;
      const ok = await confirm({
        title: "End class with unmarked students?",
        message: `${remaining} student${remaining === 1 ? "" : "s"} still unmarked. End class anyway?\n\nUnmarked students will stay unmarked — an admin alert will be raised so someone can follow up.`,
        confirmText: "End class",
        cancelText: "Keep marking",
        variant: "warning",
      });
      if (!ok) return;
    }
    const records: any[] = [];
    session.subjects.forEach((g) =>
      g.students.forEach((s) => {
        const status = marks[buildKey(s.student_id, g.subject_id)];
        if (!status || status === "not_marked") return;
        records.push({ student_id: s.student_id, subject_id: g.subject_id, status });
      })
    );
    try {
      await submitSession({ occurrenceId, records, end_class: true, end_source: "manual" }).unwrap();
      setToast({ msg: "✅ Class ended.", severity: "success" });
      setTimeout(() => navigate("/attendance"), 1500);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to end class.", severity: "error" });
    }
  };

  const openLateModal = (s: StudentRow, group: SubjectGroup) => {
    if (completed) return;
    setLateModal({
      studentId: s.student_id, subjectId: group.subject_id,
      studentName: s.name, subjectName: group.subject_name,
      arrivedAt: new Date().toTimeString().slice(0, 5), reason: "",
    });
  };
  const submitLateModal = async () => {
    if (!lateModal) return;
    if (completed) return;
    try {
      await saveLateArrival({
        occurrenceId,
        student_id: lateModal.studentId,
        subject_id: lateModal.subjectId,
        arrived_at: lateModal.arrivedAt,
        reason: lateModal.reason,
      }).unwrap();
      setMarks((m) => ({ ...m, [buildKey(lateModal.studentId, lateModal.subjectId)]: "late" }));
      const name = lateModal.studentName;
      setLateModal(null);
      setToast({ msg: `Late slip saved for ${name}`, severity: "success" });
      await refetch();
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to save late arrival.", severity: "error" });
    }
  };

  const openLateSlipPdf = (studentId: number, subjectId: number | null) => {
    const token = localStorage.getItem("token") || "";
    const q = subjectId != null ? `?subject_id=${subjectId}` : "";
    fetch(`${API_URL}/class-sessions/${occurrenceId}/attendance/late-slip/${studentId}${q}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => setToast({ msg: "Failed to open late slip.", severity: "error" }));
  };

  const persistNote = async (studentId: number) => {
    if (completed) return;
    try {
      await saveNote({
        occurrenceId,
        student_id: studentId,
        note: notes[studentId] || "",
      }).unwrap();
      setToast({ msg: "Note saved.", severity: "success" });
      setNoteOpen(null);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to save note.", severity: "error" });
    }
  };

  const openEmailModalFor = (s: StudentRow) => {
    setEmailModal({
      studentId: s.student_id,
      name: s.name,
      subject: `Regarding ${s.name}`,
      body: "",
    });
  };
  const sendQuickEmail = async () => {
    if (!emailModal) return;
    if (!emailModal.subject.trim() || !emailModal.body.trim()) {
      setToast({ msg: "Subject and message are required.", severity: "error" });
      return;
    }
    try {
      await sendMessage({
        occurrenceId,
        recipients: "parents",
        student_ids: [emailModal.studentId],
        subject: emailModal.subject.trim(),
        message: emailModal.body.trim(),
        template: "custom",
      }).unwrap();
      setToast({ msg: `📧 Email sent to ${emailModal.name}'s parent`, severity: "success" });
      setEmailModal(null);
    } catch (e: any) {
      setToast({ msg: e?.data?.message || "Failed to send email.", severity: "error" });
    }
  };

  const sendAbsenceNotice = (studentId: number) => {
    setShowMessagePanel(true);
    setMsgPrefill({
      open: true, recipients: "parents", studentIds: [studentId],
      template: "absent", version: Date.now(),
    });
    setTimeout(() => document.getElementById("send-message-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // ─────────────── guards ─────────────── //
  if (isLoading || isFetching) {
    return (
      <div style={{ background: "#f0f4f8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 48, height: 48, border: "4px solid #1b3a6b", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  const anyErr: any = error;
  const apiMessage =
    (anyErr && (anyErr.data?.message || anyErr.error || anyErr.status)) ||
    (data && (data as any).status === false ? (data as any).message : null);

  if (anyErr || apiMessage || !session) {
    // Distinguish an authorization denial from a transient load
    // failure. 403 means "not yours to open" — retry won't help,
    // and offering it invites the teacher to hammer the endpoint.
    // For 4xx-auth we surface the friendly message and only offer
    // Go Back. For 5xx / network / unknown errors keep Retry.
    const httpStatus = Number(anyErr?.status ?? anyErr?.originalStatus ?? 0);
    const isAuthDenial = httpStatus === 401 || httpStatus === 403;
    const title = isAuthDenial
      ? "You don't have access to this class session"
      : "Could not load attendance session";
    const detail = isAuthDenial
      ? "This class session isn't assigned to your account. If you believe this is wrong, contact your administrator."
      : String(apiMessage || "Request failed.");
    return (
      <div style={{ background: "#f0f4f8", minHeight: "100vh", padding: 40, textAlign: "center" }}>
        <div style={{ maxWidth: 480, margin: "80px auto", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 40 }}>{isAuthDenial ? "🔒" : "⚠️"}</div>
          <div style={{ fontWeight: 700, color: "#991b1b", fontSize: 16, marginTop: 8 }}>{title}</div>
          <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 6 }}>{detail}</div>
          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 10 }}>Session ID: {occurrenceId}</div>
          <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 8 }}>
            {!isAuthDenial && (
              <button onClick={() => refetch()} style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10, padding: "8px 18px", fontWeight: 600, cursor: "pointer" }}>Retry</button>
            )}
            <button onClick={() => navigate(returnUrl)} style={{ background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 600, cursor: "pointer" }}>← {backLabel}</button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────── computed labels ─────────────── //
  // Top-of-page class label. When the backend emits structured
  // grade_subjects, render "Grade 3: English, Science · Grade 4: Math"
  // so the header reflects real grade→subjects mappings instead of a
  // flat "3, 4, 5 · English and Math, English and Science, Math, ..."
  // sentence that mixes grades and subjects without pairing.
  const className = (() => {
    const gs = session.grade_subjects;
    if (Array.isArray(gs) && gs.length > 0) {
      return gs
        .map((b) => `Grade ${b.grade.name}: ${b.subjects.map((s) => s.name).join(", ")}`)
        .join(" · ");
    }
    // Legacy fallback (should only fire before backend deploy).
    const subjectLine = session.subjects.map((s) => s.subject_name).join(", ");
    return `${session.grade ? session.grade + " · " : ""}${subjectLine}`;
  })();
  // Batch 16 — was `new Date(session.scheduled_date).toLocaleDateString(...)`
  // which parses YYYY-MM-DD as UTC midnight → previous day west of UTC.
  // presentDateOnly reads the local Y/M/D components, so the header now
  // agrees with the app-bar and the pre-start card.
  const dateLabel = presentDateOnly(session.scheduled_date, { month: "short", day: "numeric", year: "numeric" });
  const startTimeLabel = formatTime(session.scheduled_start);
  const endTimeLabel = formatTime(session.scheduled_end);
  const actualStartLabel = session.actual_start_at ? formatTime(session.actual_start_at) : "";
  // `formatCountdown` in this scope expects SECONDS (magnitude-aware
  // "N hours N minutes"). Passing raw ms here previously turned 21 min
  // remaining into "14 days 14 hours" via the shadowed module-scope
  // helper of the same name that used to accept ms.
  const timeRemainingLabel = remainingMs > 0
    ? formatCountdown(Math.floor(remainingMs / 1000))
    : "Class Ended";
  const timeElapsedLabel = actualStartMs > 0 ? formatElapsed(effectiveNowForElapsed - actualStartMs) : "";
  // Use the normalised pair-grouping — one card per (grade, subject).
  // See displayGroups useMemo above for shape and back-compat fallback.
  const groupsWithStudents = displayGroups;

  // ─────────────── render ─────────────── //
  return (
    <div style={{ background: "#f0f4f8", minHeight: "100vh", fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", display: "flex", flexDirection: "column" }}>
      {/* CLASS-SUMMARY HEADER
          Grows vertically with content (multi-grade classes render one
          line per grade). `alignItems: flex-start` + `minWidth: 0` on
          the text column keep the avatar and back arrow pinned to the
          top while text wraps cleanly. Scrolls with the page in normal
          document flow — not sticky. z-index dropped along with the
          sticky rule; nothing needs to stack above the roster cards. */}
      <div style={{
        background: "#1b3a6b",
        padding: "12px 32px", minHeight: 64,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, minWidth: 0, flex: 1 }}>
          <button onClick={() => navigate(returnUrl)} style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
            width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18,
            flexShrink: 0, marginTop: 2,
          }}>←</button>
          <img src={getAvatar(session.staff_name || "?", null, 48)} alt="" style={{
            width: 38, height: 38, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)",
            flexShrink: 0, marginTop: 2,
          }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            {Array.isArray(session.grade_subjects) && session.grade_subjects.length > 0 ? (() => {
              const { gradesLabel, mappingsCount } = summarizeGradesCompact(session.grade_subjects!);
              const gradeCount = session.grade_subjects!.length;
              const uniqueSubjectCount = new Set(
                session.grade_subjects!.flatMap((g) => g.subjects.map((s) => s.id))
              ).size;
              // Simple mappings (1 grade + ≤3 subjects) stay inline as
              // "Grade 5 — Math, Science" so the hero still reads at a
              // glance. Anything richer collapses to a count summary
              // with the modal as the escape hatch.
              const singleGrade = session.grade_subjects!.length === 1
                ? session.grade_subjects![0]
                : null;
              const inlineText = singleGrade && singleGrade.subjects.length <= 3
                ? `${gradesLabel} — ${singleGrade.subjects.map((s) => s.name).join(", ")}`
                : `${gradeCount} Grade${gradeCount === 1 ? "" : "s"} · ${uniqueSubjectCount} Subject${uniqueSubjectCount === 1 ? "" : "s"}`;
              return (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, overflowWrap: "anywhere", minWidth: 0 }}>
                    {inlineText}
                  </div>
                  <button
                    onClick={() => setSubjectsModalOpen(true)}
                    aria-label="View grades and subjects for this class"
                    style={{
                      background: "rgba(255,255,255,0.14)", color: "#fff",
                      border: "1px solid rgba(255,255,255,0.35)", borderRadius: 8,
                      padding: "4px 10px", fontSize: 11.5, fontWeight: 700,
                      cursor: "pointer", whiteSpace: "nowrap", height: 26,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    📚 View Grades &amp; Subjects
                  </button>
                </div>
              );
            })() : (
              // Back-compat: single-line class name if backend hasn't
              // shipped the structured field yet.
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, overflowWrap: "anywhere" }}>{className}</div>
            )}
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 8 }}>
              {session.staff_name || "—"} · {dateLabel}
            </div>
          </div>
        </div>
        {/* Batch 16 — small header timer pill removed; the prominent
            Timer Card below is now the single source of truth for time
            state. Kept the header lean so date/staff/class stay legible. */}
      </div>

      {/* SESSION-DONE BANNER (>24h old, only before Start is clicked) */}
      {sessionStatus === "session_done" && !session.actual_start_at && (
        <div style={{
          background: "#f1f5f9", borderLeft: "4px solid #94a3b8",
          padding: "12px 32px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 20 }}>📅</span>
          <div>
            <strong style={{ color: "#475569" }}>Session Already Completed</strong>
            <span style={{ color: "#64748b", marginLeft: 8, fontSize: 13 }}>
              This class was scheduled for {dateLabel} at {startTimeLabel}.
              You can still submit attendance for record purposes.
            </span>
          </div>
        </div>
      )}

      {/* LATE START BANNER (10 min – 24h, only before Start is clicked) */}
      {sessionStatus === "late" && !session.actual_start_at && (
        <div style={{
          background: "#fef2f2", borderLeft: "4px solid #ef4444",
          padding: "12px 32px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <strong style={{ color: "#991b1b" }}>Late Start</strong>
            <span style={{ color: "#b91c1c", marginLeft: 8 }}>
              You are starting {formatLateDuration(currentLateMinutes)}
              (Scheduled: {startTimeLabel})
            </span>
          </div>
        </div>
      )}

      {/* STAT CARDS ROW — Batch 16: Unmarked is a real bucket now.
          Before the fix the counters read "Present 2 / Absent 0 / Late 0"
          on an untouched roster because the hydrate silently promoted
          every unmarked student to Present. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, padding: "24px 32px 0" }}>
        {[
          { label: "TOTAL",        value: String(stats.total),           color: "#1b3a6b", bg: "#eef2ff" },
          { label: "UNMARKED",     value: String(stats.unmarked),        color: "#64748b", bg: "#f1f5f9" },
          { label: "PRESENT",      value: String(stats.present),         color: "#16a34a", bg: "#f0fdf4" },
          { label: "ABSENT",       value: String(stats.absent),          color: "#dc2626", bg: "#fef2f2" },
          { label: "LATE",         value: String(stats.late),            color: "#d97706", bg: "#fffbeb" },
          { label: "LATE MINUTES", value: `${stats.lateMinutes}m`,       color: "#7c3aed", bg: "#f5f3ff" },
        ].map((c) => (
          <div key={c.label} style={{
            background: c.bg, borderRadius: 14, padding: "18px 20px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: `1px solid ${c.color}22`,
          }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.8px", color: c.color, opacity: 0.7, marginTop: 6, textTransform: "uppercase" }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* CLASS INFO CARD */}
      <div style={{
        margin: "20px 32px 0", background: "#fff", borderRadius: 16, padding: "20px 24px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0, flex: 1 }}>
          {/* Academic Coverage — compact one-liner + button. Full
             grade→subject breakdown lives inside the shared
             GradeSubjectsDialog opened from either surface. Kept
             deliberately short so the operational KPIs above and
             the Time/Students blocks below stay grouped. */}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
              Academic Coverage
            </div>
            {Array.isArray(session.grade_subjects) && session.grade_subjects.length > 0 ? (() => {
              const gradeCount = session.grade_subjects!.length;
              const uniqueSubjectCount = new Set(
                session.grade_subjects!.flatMap((g) => g.subjects.map((s) => s.id))
              ).size;
              const singleGrade = gradeCount === 1 ? session.grade_subjects![0] : null;
              const compactText = singleGrade && singleGrade.subjects.length <= 3
                ? `${summarizeGradesCompact(session.grade_subjects!).gradesLabel} — ${singleGrade.subjects.map((s) => s.name).join(", ")}`
                : `${gradeCount} Grade${gradeCount === 1 ? "" : "s"} · ${uniqueSubjectCount} Subject${uniqueSubjectCount === 1 ? "" : "s"}`;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16 }}>📚</span>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1b3a6b", minWidth: 0 }}>
                    {compactText}
                  </div>
                  <button
                    onClick={() => setSubjectsModalOpen(true)}
                    aria-label="View grades and subjects for this class"
                    style={{
                      background: "#eff6ff", color: "#1b3a6b",
                      border: "1px solid #dbeafe", borderRadius: 8,
                      padding: "5px 12px", fontSize: 12, fontWeight: 700,
                      cursor: "pointer", whiteSpace: "nowrap", height: 30,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    📚 View Grades &amp; Subjects
                  </button>
                </div>
              );
            })() : (
              <div style={{ fontWeight: 700, fontSize: 16, color: "#1b3a6b", overflowWrap: "anywhere" }}>
                📚 {className}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
            <InfoBlock label="Time" value={<>🕐 {startTimeLabel} – {endTimeLabel}</>} />
            {session.location && <InfoBlock label="Location" value={<>📍 {session.location}</>} />}
            <InfoBlock label="Students" value={<>👥 {stats.total} enrolled</>} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {started && stats.total > 0 && (
            <button
              onClick={resetAllPresent}
              title="Mark every unmarked / absent student Present (Late Check-Ins are preserved)"
              style={{
                background: "#f0fdf4", color: "#16a34a",
                border: "1px solid #bbf7d0", borderRadius: 10,
                padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >
              ✅ Mark All Present
            </button>
          )}

          {/* Batch 16 — Start / End actions moved to the dedicated
              Timer Card below (see TIMER CARD block). Info card keeps
              only class metadata + the Mark All Present shortcut so
              the primary action lives in one place. */}
        </div>
      </div>

      {/* ══════════ TIMER CARD ══════════
          Visual states (same height each so the layout never jumps):
            (0) Pre-start / grace   — informational label + Start button
            (1) Running (countdown) — HH:MM:SS ticking DOWN to scheduled
                                      end; End Class button
            (2) Overtime            — auto-end mutation is either firing
                                      this second or the cron will close
                                      it within a minute; End Now button
                                      still available for the fast path
            (3) Completed           — honest `ended_by` source rendered
                                      from the BE (manual / system)
          COUNTDOWN maths: `remainingMs = scheduledEndMs - alignedNow`.
          Actual start time never enters the calculation — a late start
          leaves the scheduled end fixed. Server-aligned time defends
          against a wrong client clock. */}
      {(() => {
        const completed = !!session.actual_end_at;
        const overtime = started && !completed && remainingMs <= 0;
        const overtimeMs = overtime ? Math.max(0, alignedNow - scheduledEndMs) : 0;
        const overtimeLabel = (() => {
          const s = Math.floor(overtimeMs / 1000);
          const m = Math.floor(s / 60);
          const rs = s % 60;
          if (m >= 60) return `${Math.floor(m / 60)}h ${(m % 60).toString().padStart(2, "0")}m`;
          return `${m}:${rs.toString().padStart(2, "0")}`;
        })();
        // Card colour and big-number colour resolve from the current state.
        const cardBg = completed ? "linear-gradient(135deg,#065f46,#047857)"    // deep emerald — closed
                      : overtime ? "#7f1d1d"                                     // deep red — overrun
                      : started ? "linear-gradient(135deg,#0f172a,#1b3a6b)"      // running: navy
                      : "linear-gradient(135deg,#1e293b,#334155)";               // pre-start: slate
        const bigColor = completed ? "#d1fae5" : overtime ? "#fecaca" : timerColor;

        // Big number. Running state now shows a live HH:MM:SS countdown
        // (tabular-nums keeps the width stable each second) — the old
        // "1 hour 43 minutes elapsed" phrasing has moved out entirely
        // (the small label carries context, the big label carries the
        // operational countdown).
        const bigLabel = completed
          ? "Class Completed"
          : overtime
            ? `OVERTIME ${overtimeLabel}`
            : started
              ? formatCountdownHms(remainingMs)
              : finishedNotStarted
                ? "Class time finished"
                : beforeGrace
                  ? "Class has not opened yet"
                  : (insideGrace
                      ? "Ready — start early"
                      : (sessionStatus === "late" ? "Late Start" : "Ready to start"));
        // Class-start lateness — how late STAFF started the class. Kept
        // separate from the "Late Minutes" KPI (which counts STUDENT
        // arrival lateness). Shown only for a class actually running late,
        // so on-time and early starts stay clean.
        const classStartLateMs = actualStartMs > 0 && scheduledStartMs > 0
          ? actualStartMs - scheduledStartMs
          : 0;
        const classStartLateLabel = classStartLateMs >= 60_000
          ? formatElapsed(classStartLateMs)
          : "";
        // Honest completed-source line. `ended_by` comes from the BE
        // — 'manual' (teacher hit End Class), 'system' (cron OR the
        // browser countdown auto-fire, both label as 'system'). We
        // do NOT invent source text if the field is missing.
        const endedAtLabel = session.actual_end_at ? formatTime(session.actual_end_at) : "";
        const completedSourceLine = completed
          ? (session.ended_by === "system"
              ? `Ended automatically at ${endedAtLabel}`
              : session.ended_by === "manual"
                ? `Ended manually at ${endedAtLabel}`
                : `Ended at ${endedAtLabel}`)
          : "";
        // Countdown subtitle: "TIME REMAINING · Ends 12:00 PM".
        const countdownSubtitle = started && !completed && !overtime
          ? `TIME REMAINING · Ends ${endTimeLabel}`
          : "";
        const smallLabel = completed
          ? `${completedSourceLine} · Scheduled ${startTimeLabel} – ${endTimeLabel}${session.actual_start_at ? ` · Started at ${formatTime(session.actual_start_at)}` : ""}`
          : overtime
            ? `Scheduled end passed · started at ${actualStartLabel}${classStartLateLabel ? ` · started ${classStartLateLabel} late` : ""}`
            : started
              ? `Started at ${actualStartLabel}${classStartLateLabel ? ` · started ${classStartLateLabel} late` : ""} · Scheduled end ${endTimeLabel}`
              : finishedNotStarted
                ? `${scheduledWindowLabel} · session ended without attendance`
                : beforeGrace
                  ? `Attendance opens at ${startTimeLabel}`
                  : `${scheduledWindowLabel} · opens 15 min early`;

        // Action button
        let actionButton: React.ReactNode = null;
        if (completed) {
          actionButton = null;
        } else if (!started) {
          if (finishedNotStarted) {
            // Scheduled end has passed and no one ever started the class.
            // Start is not offered — there is nothing this page can honestly
            // record for a class that already ended. BE enforces the same
            // rule (see AttendanceSessionController::start after_end gate).
            actionButton = null;
          } else if (beforeGrace) {
            actionButton = (
              <button disabled style={{
                background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12,
                padding: "14px 32px", fontWeight: 800, fontSize: 15,
                cursor: "not-allowed", whiteSpace: "nowrap",
              }}>
                ⏳ Waiting for grace window
              </button>
            );
          } else {
            const startLabel =
              sessionStatus === "session_done" ? "📋 Submit Past Attendance"
              : sessionStatus === "late"       ? `🔴 Start (${formatLateDuration(currentLateMinutes)})`
              : insideGrace                     ? "🟢 Start Early"
              : "🟢 Start Attendance";
            const startBg = sessionStatus === "session_done" ? "#7c3aed"
                          : sessionStatus === "late"         ? "#dc2626"
                          : "#16a34a";
            actionButton = (
              <button onClick={handleStart} disabled={startState.isLoading} style={{
                background: startBg, color: "#fff", border: "none",
                borderRadius: 12, padding: "14px 32px", fontWeight: 800, fontSize: 15,
                cursor: "pointer", whiteSpace: "nowrap",
                boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
              }}>
                {startLabel}
              </button>
            );
          }
        } else {
          actionButton = (
            <button
              onClick={handleEndClass}
              disabled={submitState.isLoading}
              style={{
                background: overtime ? "#fef2f2" : "#dc2626",
                color: overtime ? "#7f1d1d" : "#fff",
                border: overtime ? "2px solid #fecaca" : "none",
                borderRadius: 12, padding: "14px 32px", fontWeight: 800, fontSize: 15,
                cursor: "pointer", whiteSpace: "nowrap",
                boxShadow: overtime
                  ? "0 6px 22px rgba(254,202,202,0.4)"
                  : "0 6px 18px rgba(220,38,38,0.45)",
              }}
            >
              {overtime ? "🔴 End Class Now" : "🔴 End Class"}
            </button>
          );
        }

        return (
          <div style={{
            margin: "20px 32px 0",
            background: cardBg, borderRadius: 16,
            padding: "24px 28px",
            minHeight: 148,   // ← same height in every state; layout never jumps
            boxShadow: "0 4px 18px rgba(15,23,42,0.15)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 20, color: "#fff", flexWrap: "wrap",
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              {(!started && !completed && !overtime && !finishedNotStarted && beforeGrace) ? (
                // Pre-class layout: the LIVE countdown is the hero
                // number. Time source is the same `alignedNow`
                // (server-time-corrected wall clock) + `scheduledStartMs`
                // the Start-button gate already reads, so the visual
                // is always synchronised with the actual actionable
                // threshold. The 1-second tick from the existing
                // setInterval at ~L299 drives updates; when
                // scheduledStart is crossed the state selector above
                // flips out of `beforeGrace` and this branch stops
                // rendering — the "Ready to start" / "Ready — start
                // early" state takes over without a refetch.
                <>
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.4px", textTransform: "uppercase",
                  }}>
                    Class has not opened yet
                  </div>
                  <div style={{
                    marginTop: 10,
                    fontSize: "clamp(34px, 6vw, 52px)",
                    fontWeight: 900, lineHeight: 1,
                    color: bigColor, fontVariantNumeric: "tabular-nums",
                  }}>
                    {formatCountdownCompact(scheduledStartMs - alignedNow)}
                  </div>
                  <div style={{
                    marginTop: 6, fontSize: 11, fontWeight: 700,
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.6px", textTransform: "uppercase",
                  }}>
                    Until class starts
                  </div>
                  {/* `smallLabel` (Starts at 11:30 AM ·…) still
                      renders below via the shared block after this
                      ternary — no duplication. */}
                </>
              ) : (
              <div style={{
                fontSize: 44, fontWeight: 900, lineHeight: 1,
                color: bigColor, fontVariantNumeric: "tabular-nums",
                letterSpacing: overtime ? "0.5px" : undefined,
              }}>
                {bigLabel}
              </div>
              )}
              {countdownSubtitle && (
                <div style={{
                  marginTop: 6, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.65)",
                  letterSpacing: "0.6px", textTransform: "uppercase",
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                }}>
                  <span>{countdownSubtitle}</span>
                  {endingSoon && (
                    <span style={{
                      background: "rgba(254,202,202,0.2)", color: "#fecaca",
                      border: "1px solid rgba(254,202,202,0.35)",
                      borderRadius: 999, padding: "2px 10px", fontSize: 10, fontWeight: 800,
                      letterSpacing: "0.4px",
                    }}>Ending soon</span>
                  )}
                </div>
              )}
              <div style={{
                marginTop: 10, fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 500,
              }}>
                {smallLabel}
              </div>
              {overtime && (
                <div style={{
                  marginTop: 10, fontSize: 12, fontWeight: 700, color: "#fecaca",
                  background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: "6px 12px",
                  display: "inline-block",
                }}>
                  ⚠️ This class has run past its scheduled end. Closing now…
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0 }}>
              {actionButton}
            </div>
          </div>
        );
      })()}

      {/* EMPTY STATE */}
      {stats.total === 0 && (
        <div style={{
          margin: "20px 32px 0", background: "#fff", borderRadius: 16, padding: 32,
          textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 44 }}>👥</div>
          <div style={{ fontWeight: 700, color: "#1b3a6b", fontSize: 16, marginTop: 8 }}>
            No students enrolled in this class yet.
          </div>
          <button onClick={() => navigate(`/class-schedules/${session.class_id}`)} style={{
            marginTop: 14, background: "#1b3a6b", color: "#fff", border: "none",
            borderRadius: 10, padding: "10px 20px", fontWeight: 600, cursor: "pointer",
          }}>+ Add Student</button>
        </div>
      )}

      {/* Batch 16 — pre-start notice, gated on `beforeGrace` not just
          `!started`. My Batch 14 code showed the lock card any time the
          teacher hadn't clicked Start yet, including 3 minutes after
          scheduled start when the class was ready to run. Now the lock
          only shows when the grace window hasn't opened; once inside
          the window (or past scheduled start), the roster reveals but
          stays uninteractive until Start is clicked (submit-disable
          still enforced by the sticky footer and the backend). */}
      {!started && beforeGrace && stats.total > 0 && (
        // Slimmed: the hero card above already carries the live
        // countdown and scheduled start time, so this card just
        // explains the unlock behaviour and shows the roster size.
        // No duplicated countdown/start-time.
        <div style={{
          margin: "20px 32px 0", background: "#fff", borderRadius: 16,
          padding: "18px 22px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0", display: "flex", alignItems: "center",
          gap: 12,
        }}>
          <div style={{ fontSize: 22, lineHeight: 1 }}>🔒</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, color: "#1b3a6b", fontSize: 14 }}>
              Attendance will unlock automatically when the class becomes available.
            </div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
              The student roster will appear here once attendance opens · {stats.total} student{stats.total === 1 ? "" : "s"} enrolled.
            </div>
          </div>
        </div>
      )}

      {/* STUDENT LIST GROUPED BY SUBJECT — visible inside the grace
          window and after scheduled start so the teacher can see who's
          expected, but interactive only after Start (row taps are
          inert until `started`; submit is disabled). */}
      {(started || !beforeGrace) && (
      <div style={{ padding: "20px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        {completed && (
          <div
            role="status"
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#475569",
            }}
          >
            <span style={{ fontSize: 16 }}>🔒</span>
            <span>
              <strong style={{ color: "#1e293b", fontWeight: 700 }}>Attendance is locked.</strong>{" "}
              This class has been completed — the recorded values are read-only.
            </span>
          </div>
        )}
        {groupsWithStudents.length === 0 && (
          <div style={{
            background: "#fff", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            padding: 32, textAlign: "center", color: "#64748b",
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
              No students are assigned to this class session.
            </div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Add students to the class roster to record attendance here.
            </div>
          </div>
        )}
        {groupsWithStudents.map((group) => {
          // Card header shows the REAL (grade, subject) pair — one card
          // per pair. Title-cased for readability instead of ALL CAPS.
          // When grade context is unavailable (legacy fallback where
          // `groups` was synthesised from `subjects`), just show the
          // subject name — no "3, 4, 5" flat-list soup.
          const hasGrade = !!(group.grade_name && group.grade_id);
          const headerLabel = hasGrade
            ? `Grade ${group.grade_name} — ${group.subject_name}`
            : group.subject_name;
          return (
          <div key={group.pair_id ?? `${group.subject_id ?? "gen"}-${group.grade_id ?? "0"}`} style={{
            background: "#fff", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0", overflow: "hidden",
          }}>
            {/* subject header */}
            <div style={{
              background: "#f8fafc", padding: "14px 24px", borderBottom: "1px solid #e2e8f0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div style={{ fontWeight: 700, color: "#1b3a6b", fontSize: 14 }}>
                📚 {headerLabel}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {/* Batch 16 — inert until the teacher has clicked Start.
                    The roster is visible in the grace window so the
                    teacher can see who's expected, but bulk-mark buttons
                    are disabled so nothing writes before the class
                    officially begins. */}
                {(() => {
                  const bulkDisabled = !started || completed;
                  const bulkTitle = completed
                    ? "Attendance is locked because this class has been completed."
                    : !started
                      ? "Start the class before marking"
                      : undefined;
                  return (
                    <>
                      <button
                        onClick={() => markAllInGroup(group, "present")}
                        disabled={bulkDisabled}
                        title={bulkTitle}
                        style={{
                          background: !bulkDisabled ? "#f0fdf4" : "#f1f5f9",
                          border: !bulkDisabled ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                          color: !bulkDisabled ? "#16a34a" : "#94a3b8",
                          borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: 12,
                          cursor: !bulkDisabled ? "pointer" : "not-allowed",
                        }}
                      >
                        ✅ All Present
                      </button>
                      <button
                        onClick={() => markAllInGroup(group, "absent")}
                        disabled={bulkDisabled}
                        title={bulkTitle}
                        style={{
                          background: !bulkDisabled ? "#fef2f2" : "#f1f5f9",
                          border: !bulkDisabled ? "1px solid #fecaca" : "1px solid #e2e8f0",
                          color: !bulkDisabled ? "#dc2626" : "#94a3b8",
                          borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: 12,
                          cursor: !bulkDisabled ? "pointer" : "not-allowed",
                        }}
                      >
                        ❌ All Absent
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* rows */}
            {group.students.length === 0 && (
              <div style={{ padding: 24, color: "#94a3b8", textAlign: "center" }}>
                No students in this subject.
              </div>
            )}
            {group.students.map((student, i) => {
              const key = buildKey(student.student_id, group.subject_id);
              const status = marks[key] || "not_marked";
              // `isCanonicalMirror` = the same (student, subject) has
              // already been rendered in an earlier group. Because the
              // DB stores ONE attendance row per (occurrence, student,
              // subject), all three cards share the same state key
              // `${student}:${subject}` — flipping any of them updates
              // the others automatically. The mirror flag is used only
              // to gate the yellow "Send absence notice" prompt so we
              // never queue three SMS for the same canonical absence.
              const isCanonicalMirror = !!(student as StudentRowDedup)._dupeOfHeader;
              const rowBg =
                status === "present" ? "#f0fdf4"
                : status === "absent"  ? "#fef2f2"
                : status === "late"    ? "#fffbeb"
                : "#fff";
              const showAbsentPrompt = !isCanonicalMirror && status === "absent" && !dismissedPrompts[key];
              const avatarBorder =
                status === "present" ? "#86efac"
                : status === "absent"  ? "#fca5a5"
                : status === "late"    ? "#fde68a"
                : "#e2e8f0";
              // A completed session freezes every row into read-only state
              // (no toggling present/absent, no opening the late modal, no
              // opening the note editor, no changing a late back to present).
              const clickable = status !== "late" && !completed;
              const lateMins = computeLateMinutes(scheduledStartMs, student.arrived_at);
              return (
                <div key={key}>
                  <div
                    onClick={() => clickable && toggleRow(student.student_id, group.subject_id)}
                    role={clickable ? "button" : undefined}
                    style={{
                      display: "flex", alignItems: "center", padding: "16px 24px", gap: 16,
                      borderBottom: i < group.students.length - 1 ? "1px solid #f1f5f9" : "none",
                      background: rowBg, transition: "background 0.15s",
                      cursor: clickable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    <img
                      src={getAvatar(student.name, student.avatar, 44)}
                      alt={student.name}
                      style={{
                        width: 44, height: 44, borderRadius: "50%",
                        border: `2px solid ${avatarBorder}`, flexShrink: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0, pointerEvents: "none" }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>{student.name}</div>
                      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                        {session.grade ? `${session.grade} · ` : ""}Student
                      </div>
                      {status === "late" && student.arrived_at && (
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
                          Scheduled {startTimeLabel} · Arrived {formatTime(student.arrived_at)}
                          <span
                            style={{
                              color: lateMins >= 30 ? "#dc2626" : "#d97706",
                              fontWeight: 600, marginLeft: 4,
                            }}
                          >
                            ({formatLateDuration(lateMins)})
                          </span>
                          {student.late_reason ? (
                            <span style={{ color: "#94a3b8" }}> · {student.late_reason}</span>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {/* Big tappable status pill */}
                    <div style={{ pointerEvents: "none" }}>
                      {status === "present" && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "#16a34a", color: "#fff",
                          borderRadius: 12, padding: "10px 20px",
                          fontWeight: 700, fontSize: 14,
                          boxShadow: "0 4px 12px rgba(22,163,74,0.3)",
                        }}>
                          <span style={{ fontSize: 18 }}>✅</span> Present
                          <span style={{ fontSize: 11, opacity: 0.85, marginLeft: 4 }}>
                            tap to mark absent
                          </span>
                        </div>
                      )}
                      {status === "absent" && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "#dc2626", color: "#fff",
                          borderRadius: 12, padding: "10px 20px",
                          fontWeight: 700, fontSize: 14,
                          boxShadow: "0 4px 12px rgba(220,38,38,0.3)",
                        }}>
                          <span style={{ fontSize: 18 }}>❌</span> Absent
                          <span style={{ fontSize: 11, opacity: 0.85, marginLeft: 4 }}>
                            tap to undo
                          </span>
                        </div>
                      )}
                      {status === "late" && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "#d97706", color: "#fff",
                          borderRadius: 12, padding: "10px 20px",
                          fontWeight: 700, fontSize: 14,
                          boxShadow: "0 4px 12px rgba(217,119,6,0.3)",
                        }}>
                          <span style={{ fontSize: 18 }}>🕐</span>
                          Late · {lateMins}m
                        </div>
                      )}
                    </div>

                    {/* Late Slip — sits immediately after the Absent pill,
                        before the icon strip. */}
                    {status === "absent" && !completed && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openLateModal(student, group);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#ea580c";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff7ed";
                          e.currentTarget.style.color = "#ea580c";
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: 5,
                          background: "#fff7ed", color: "#ea580c",
                          border: "2px solid #fed7aa",
                          borderRadius: 10, padding: "9px 16px",
                          fontWeight: 700, fontSize: 13, cursor: "pointer",
                          whiteSpace: "nowrap", transition: "all 0.15s",
                          marginLeft: 8,
                        }}
                      >
                        🕐 Late Slip
                      </button>
                    )}

                    {/* Icon actions — stopPropagation so clicks don't toggle the row */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 4 }}
                    >
                      <IconAction
                        title="View Student Profile"
                        bg="#eff6ff" fg="#2563eb" border="#bfdbfe" hoverBg="#2563eb"
                        onClick={() => navigate(`/student-history/${student.student_id}`)}
                      >👤</IconAction>
                      <IconAction
                        title="Email Parent"
                        bg="#f0fdf4" fg="#16a34a" border="#bbf7d0" hoverBg="#16a34a"
                        onClick={() => openEmailModalFor(student)}
                      >📧</IconAction>
                      <IconAction
                        title="Attendance History"
                        bg="#fdf4ff" fg="#7c3aed" border="#e9d5ff" hoverBg="#7c3aed"
                        onClick={() => setHistoryStudent({ id: student.student_id, name: student.name })}
                      >📋</IconAction>
                      {!completed && (
                        <IconAction
                          title="Add Session Note"
                          bg="#fffbeb" fg="#d97706" border="#fde68a" hoverBg="#d97706"
                          onClick={() =>
                            setNoteOpen(noteOpen === student.student_id ? null : student.student_id)
                          }
                        >📝</IconAction>
                      )}
                      {status === "late" && (
                        <IconAction
                          title="Print Late Slip"
                          bg="#fef2f2" fg="#dc2626" border="#fecaca" hoverBg="#dc2626"
                          onClick={() => openLateSlipPdf(student.student_id, group.subject_id)}
                        >🖨️</IconAction>
                      )}
                      {status === "late" && !completed && (
                        <Tooltip
                          title="Change late status"
                          placement="top"
                          arrow
                          enterDelay={200}
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#0f172a", color: "#ffffff",
                                fontSize: 12, fontWeight: 500, px: 1.25, py: 0.75,
                                borderRadius: "6px",
                                boxShadow: "0 4px 12px rgba(15,23,42,0.25)",
                                "& .MuiTooltip-arrow": { color: "#0f172a" },
                              },
                            },
                          }}
                        >
                          <button
                            onClick={() =>
                              setStatus(student.student_id, group.subject_id, "present")
                            }
                            aria-label="Change late status"
                            style={{
                              background: "none", border: "none", color: "#94a3b8",
                              fontSize: 12, cursor: "pointer", padding: "0 6px",
                            }}
                          >
                            Change
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  {/* Inline session note editor */}
                  {noteOpen === student.student_id && (
                    <div style={{
                      padding: "10px 24px 14px 84px", background: "#f8fafc",
                      borderTop: "1px solid #e2e8f0",
                    }}>
                      <textarea
                        placeholder="Add a note about this student for today's session..."
                        value={notes[student.student_id] || ""}
                        onChange={(e) =>
                          setNotes((prev) => ({ ...prev, [student.student_id]: e.target.value }))
                        }
                        autoFocus
                        style={{
                          width: "100%", border: "1px solid #e2e8f0", borderRadius: 8,
                          padding: "10px 12px", fontSize: 13, resize: "none", minHeight: 70,
                          fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                        }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#1b3a6b")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => persistNote(student.student_id)}
                          disabled={saveNoteState.isLoading}
                          style={{
                            background: "#1b3a6b", color: "#fff", border: "none",
                            borderRadius: 7, padding: "6px 16px",
                            fontSize: 12, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          💾 Save Note
                        </button>
                        <button
                          onClick={() => setNoteOpen(null)}
                          style={{
                            background: "none", border: "1px solid #e2e8f0",
                            borderRadius: 7, padding: "6px 12px",
                            fontSize: 12, color: "#94a3b8", cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {showAbsentPrompt && (
                    <div style={{
                      background: "#fef9e7", borderTop: "1px solid #fde68a",
                      padding: "10px 24px 10px 84px", display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <span style={{ fontSize: 13, color: "#92400e" }}>📨 Send absence notice to parent?</span>
                      <button onClick={() => { sendAbsenceNotice(student.student_id); setDismissedPrompts((p) => ({ ...p, [key]: true })); }} style={{
                        background: "#d97706", color: "#fff", border: "none", borderRadius: 7,
                        padding: "5px 14px", fontWeight: 600, fontSize: 12, cursor: "pointer",
                      }}>Send Now</button>
                      <button onClick={() => setDismissedPrompts((p) => ({ ...p, [key]: true }))} style={{
                        background: "none", border: "none", color: "#94a3b8", fontSize: 12, cursor: "pointer",
                      }}>Skip</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          );
        })}
      </div>
      )}

      {/* SEND MESSAGE PANEL */}
      {showMessagePanel && stats.total > 0 && (
        <div id="send-message-panel" style={{ padding: "0 32px 20px" }}>
          <SendMessagePanel
            occurrenceId={occurrenceId}
            students={allStudentsFlat}
            context={{
              className, grade: session.grade, staffName: session.staff_name,
              dateLabel, startTime: startTimeLabel, endTime: endTimeLabel,
            }}
            startCollapsed={false}
            prefill={msgPrefill}
          />
        </div>
      )}

      {/* Footer — sticks to viewport bottom when content scrolls, else sits
          naturally right below the last row (no more huge empty gap for
          short rosters). Uses position: sticky + mt: auto so the flex-column
          page pushes it to the bottom of the visible area when needed. */}
      <div style={{
        marginTop: "auto",
        position: "sticky",
        bottom: 0,
        background: "#fff",
        borderTop: "1px solid #e2e8f0",
        padding: "14px 32px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
        zIndex: 100,
      }}>
        <button onClick={() => {
          setShowMessagePanel(true);
          setTimeout(() => document.getElementById("send-message-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        }} style={{
          background: "#f8fafc", color: "#1b3a6b", border: "2px solid #1b3a6b",
          borderRadius: 12, padding: "12px 24px", fontWeight: 700, fontSize: 14, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 8,
        }}>📨 Send Message</button>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {/* Batch 16 — read from real marks only. The unmarked bucket
                shows first when non-zero so a teacher can see at a glance
                that the class isn't ready to submit. */}
            {stats.unmarked > 0 && (
              <span style={{ color: "#64748b", fontWeight: 700 }}>
                {stats.unmarked} Unmarked
              </span>
            )}
            {stats.present > 0 && (
              <span style={{ color: "#16a34a", fontWeight: 700, marginLeft: stats.unmarked > 0 ? 12 : 0 }}>
                {stats.present} Present
              </span>
            )}
            {stats.absent > 0 && (
              <span style={{ color: "#dc2626", fontWeight: 700, marginLeft: 12 }}>
                {stats.absent} Absent
              </span>
            )}
            {stats.late > 0 && (
              <span style={{ color: "#d97706", fontWeight: 700, marginLeft: 12 }}>
                {stats.late} Late
              </span>
            )}
            <span style={{ color: "#94a3b8", marginLeft: 12 }}>
              of {stats.total} students
            </span>
          </div>
          {(() => {
            // Batch 14 — submit is blocked until the session is started.
            // Backend also refuses (POST returns 422) so this button is
            // the visible half of the same rule.
            // A completed session is also read-only — the backend rejects
            // any further submit with the same 422 shape.
            const disabled = submitState.isLoading || stats.total === 0 || !started || completed;
            const label = completed
              ? "🔒 Class completed"
              : !started
                ? "🔒 Start class to submit"
                : "💾 Submit Attendance →";
            const title = completed
              ? "Attendance is locked because this class has been completed."
              : !started
                ? "You cannot submit attendance for a class that has not been started."
                : undefined;
            return (
              <button
                onClick={handleSubmit}
                disabled={disabled}
                title={title}
                style={{
                  background: disabled ? "#e2e8f0" : "#1b3a6b",
                  color: disabled ? "#94a3b8" : "#fff",
                  border: "none", borderRadius: 12,
                  padding: "12px 28px", fontWeight: 700, fontSize: 14,
                  cursor: disabled ? "not-allowed" : "pointer",
                  boxShadow: disabled ? "none" : "0 4px 14px rgba(27,58,107,0.4)",
                  transition: "all 0.2s", display: "flex", alignItems: "center", gap: 8,
                }}
              >
                {label}
              </button>
            );
          })()}
        </div>
      </div>

      {/* LATE CHECK-IN MODAL */}
      {lateModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => setLateModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 20, overflow: "hidden",
            boxShadow: "0 25px 60px rgba(0,0,0,0.2)", maxWidth: 420, width: "100%",
          }}>
            <div style={{ background: "#1b3a6b", padding: "18px 24px", color: "#fff", fontSize: 16, fontWeight: 700 }}>
              🕐 Late Check-In
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 12, padding: "16px 24px",
              background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
            }}>
              <img src={getAvatar(lateModal.studentName, null, 44)} alt="" style={{
                width: 44, height: 44, borderRadius: "50%", border: "2px solid #e2e8f0",
              }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>{lateModal.studentName}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {session.grade ? `${session.grade} · ` : ""}{lateModal.subjectName}
                </div>
              </div>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <label style={{
                display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.8px",
                textTransform: "uppercase", color: "#64748b", marginBottom: 12,
              }}>Arrival Time</label>
              <PremiumTimePicker value={lateModal.arrivedAt} onChange={(v) => setLateModal((m) => (m ? { ...m, arrivedAt: v } : m))} />
              <label style={{
                display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.8px",
                textTransform: "uppercase", color: "#64748b", marginTop: 20, marginBottom: 8,
              }}>Reason</label>
              <textarea
                value={lateModal.reason}
                onChange={(e) => setLateModal((m) => (m ? { ...m, reason: e.target.value } : m))}
                placeholder="Enter reason for late arrival..."
                style={{
                  width: "100%", minHeight: 80, border: "2px solid #e2e8f0",
                  borderRadius: 10, padding: 12, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none",
                }}
              />
            </div>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "16px 24px", borderTop: "1px solid #e2e8f0", background: "#f8fafc",
            }}>
              <button onClick={() => setLateModal(null)} style={{
                background: "#fff", color: "#1b3a6b", border: "1px solid #1b3a6b",
                borderRadius: 10, padding: "10px 24px", fontWeight: 600, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={submitLateModal} disabled={lateArrivalState.isLoading} style={{
                background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 10,
                padding: "10px 24px", fontWeight: 600, cursor: lateArrivalState.isLoading ? "not-allowed" : "pointer",
              }}>✅ Confirm Late Check-In</button>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL PARENT MODAL */}
      {emailModal && (
        <EmailParentModal
          modal={emailModal}
          setModal={setEmailModal}
          onSend={sendQuickEmail}
          loading={sendMessageState.isLoading}
          session={session}
        />
      )}

      {/* ATTENDANCE HISTORY SLIDE PANEL */}
      <AttendanceHistoryPanel
        student={historyStudent}
        onClose={() => setHistoryStudent(null)}
        navigate={navigate}
      />

      {/* TOAST */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 100, right: 24, zIndex: 300,
          background: toast.severity === "success" ? "#f0fdf4" : toast.severity === "error" ? "#fef2f2" : "#eff6ff",
          border: `1px solid ${toast.severity === "success" ? "#bbf7d0" : toast.severity === "error" ? "#fecaca" : "#bfdbfe"}`,
          color: toast.severity === "success" ? "#16a34a" : toast.severity === "error" ? "#991b1b" : "#1e40af",
          padding: "12px 20px", borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8,
        }}>
          {toast.msg}
          <button onClick={() => setToast(null)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 16, marginLeft: 8 }}>×</button>
        </div>
      )}

      {/* Full grade→subject mapping. The two card surfaces above show
          only the compact one-liner + "View Grades & Subjects"; the
          detailed grouped view lives here. Reused across Attendance,
          AttendanceRecord and this page — sort_order/canonical grade
          order + de-duped subjects handled inside the component. */}
      <GradeSubjectsDialog
        open={subjectsModalOpen}
        onClose={() => setSubjectsModalOpen(false)}
        groups={Array.isArray(session?.grade_subjects) ? session!.grade_subjects! : []}
        title="Grades & Subjects"
        subtitle={[
          session?.staff_name ? `Teacher: ${session.staff_name}` : null,
          dateLabel,
          `${startTimeLabel} – ${endTimeLabel}`,
          `${stats.total} student${stats.total === 1 ? "" : "s"}`,
        ].filter(Boolean).join(" · ")}
      />
    </div>
  );
};

// Small horizontal label/value block used in the class info card.
const InfoBlock: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({ label, value, accent }) => (
  <div>
    <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontWeight: accent ? 700 : 600, fontSize: accent ? 16 : 14, color: accent || "#1a1a2e" }}>
      {value}
    </div>
  </div>
);

// ─────────────── Inline icon action button (row actions) ─────────────── //
const IconAction: React.FC<{
  title: string;
  bg: string;
  fg: string;
  border: string;
  hoverBg: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, bg, fg, border, hoverBg, onClick, children }) => (
  // Premium tooltip via MUI, not the OS-default `title` attribute. Dark
  // navy background with a small arrow, portalled to `document.body` so
  // it never gets clipped by the attendance card. `aria-label` on the
  // button remains for screen readers — tooltip text is a visual affordance.
  <Tooltip
    title={title}
    placement="top"
    arrow
    enterDelay={200}
    slotProps={{
      tooltip: {
        sx: {
          bgcolor: "#0f172a",
          color: "#ffffff",
          fontSize: 12,
          fontWeight: 500,
          px: 1.25,
          py: 0.75,
          borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(15,23,42,0.25)",
          "& .MuiTooltip-arrow": { color: "#0f172a" },
        },
      },
    }}
  >
    <button
      onClick={onClick}
      aria-label={title}
      style={{
        width: 34, height: 34, borderRadius: 8,
        background: bg, border: `1px solid ${border}`, color: fg,
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 15, transition: "all 0.15s", padding: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.color = "#fff";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = bg;
        e.currentTarget.style.color = fg;
      }}
    >
      {children}
    </button>
  </Tooltip>
);

// ─────────────── Email Parent Modal ─────────────── //
type EmailModalState = {
  studentId: number;
  name: string;
  subject: string;
  body: string;
};
const EmailParentModal: React.FC<{
  modal: EmailModalState;
  setModal: (m: EmailModalState | null) => void;
  onSend: () => void;
  loading: boolean;
  session: SessionData;
}> = ({ modal, setModal, onSend, loading, session }) => {
  // 2026-08-25: the previous concat produced ugly walls like
  //   "Grade Pre-K, 1, 2, ... · Summer Camp, ABA Therapy,
  //    English and Math, English, ..."
  // for broad multi-grade classes because it flattened every
  // grade-subject pair into two comma-joined bags. Route through
  // the shared `formatClassLabel` helper so the modal composer uses
  // the SAME compact label the class detail hero / Class Sessions
  // rows already emit (short-form when small, "N grades · N subjects"
  // when broad). Templates that reference `{className}` therefore
  // never dump a 700-char paragraph into the message body.
  const grades = session.grade ? String(session.grade).split(",").map((g) => g.trim()).filter(Boolean) : [];
  const subjectNames = (session.subjects || []).map((s) => s.subject_name).filter(Boolean);
  const className = formatClassLabel(null, grades, subjectNames) || "Class";
  const dateLabel = presentDateOnly(session.scheduled_date, {
    month: "short", day: "numeric", year: "numeric",
  });
  const startLabel = formatTime(session.scheduled_start);
  const endLabel = formatTime(session.scheduled_end);

  // Phase 2: templates come from the tenant/global attendance set
  // (Settings → Message Templates). Admin edits invalidate the
  // "messageTemplate" cache tag so this list refreshes without a
  // page reload. Backend tokens (`{{student_name}}` etc.) are mapped
  // to the legacy `{student}`-style tokens this modal filled so the
  // fill logic below keeps working unchanged.
  const tenantTplQuery = useGetAttendanceMessageTemplatesQuery();
  const ICON_BY_KEY: Record<string, string> = {
    attendance_absent:    "📋",
    attendance_behaviour: "⚠️",
    attendance_homework:  "📚",
    attendance_progress:  "🎉",
    attendance_late:      "🕐",
  };
  const defaultTemplates = React.useMemo(() => {
    const rows: any[] = ((tenantTplQuery.data?.data as any)?.templates) || [];
    return rows.map((r) => ({
      id: `srv-${r.id}`,
      icon: ICON_BY_KEY[r.key] || "📝",
      label: r.name,
      subject: r.subject_template,
      body: r.body_template,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantTplQuery.data]);

  const [customTemplates, setCustomTemplates] = React.useState<Array<{ id: string; icon: string; label: string; subject: string; body: string }>>(() => {
    try {
      const raw = localStorage.getItem("msgTemplates");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [showAllTemplates, setShowAllTemplates] = React.useState(false);
  const [showAddTemplate, setShowAddTemplate] = React.useState(false);
  const [tplLabel, setTplLabel] = React.useState("");
  const [tplSubject, setTplSubject] = React.useState("");
  const [tplBody, setTplBody] = React.useState("");
  const allTemplates = React.useMemo(
    () => [...defaultTemplates, ...customTemplates],
    [defaultTemplates, customTemplates]
  );

  // Fill both legacy `{token}` and Phase-2 `{{token}}` placeholders
  // from session context. This is a client-side preview — the actual
  // send re-renders on the server per-recipient (see
  // AttendanceSessionController::sendMessage).
  const applyTemplate = (t: { subject: string; body: string }) => {
    const values: Record<string, string> = {
      // Phase 2 tokens
      student_name: modal.name,
      class_name:   className,
      staff_name:   session.staff_name || "",
      session_date: dateLabel,
      start_time:   startLabel,
      end_time:     endLabel,
      location:     "",
      recipient_label: "Parent/Guardian",
      attendance_status: "",
      late_minutes: "",
      // Legacy + personal-template tokens (all forms accepted)
      student: modal.name,
      parent:  "Parent/Guardian",
      class:   className,
      date:    dateLabel,
      time:    `${startLabel}${endLabel ? " – " + endLabel : ""}`,
      studentName: modal.name,
      parentName:  "Parent/Guardian",
      staffName:   session.staff_name || "",
    };
    const fill = (s: string) => {
      let out = s;
      for (const [k, v] of Object.entries(values)) {
        out = out.split(`{{${k}}}`).join(v).split(`{${k}}`).join(v);
      }
      return out;
    };
    setModal({ ...modal, subject: fill(t.subject), body: fill(t.body) });
  };

  const persistCustom = (next: typeof customTemplates) => {
    setCustomTemplates(next);
    try { localStorage.setItem("msgTemplates", JSON.stringify(next)); } catch { /* private mode */ }
  };
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={() => setModal(null)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20, width: 480, maxWidth: "100%",
          boxShadow: "0 25px 60px rgba(0,0,0,0.2)", overflow: "hidden",
        }}
      >
        <div style={{
          background: "#1b3a6b", padding: "18px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>📧 Email Parent</div>
          <button
            onClick={() => setModal(null)}
            style={{
              background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
              width: 28, height: 28, borderRadius: 6, cursor: "pointer", fontSize: 16,
            }}
          >✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px", background: "#f8fafc",
            borderRadius: 10, marginBottom: 16,
          }}>
            <img
              src={getAvatar(modal.name, null, 40)}
              alt=""
              style={{ width: 38, height: 38, borderRadius: "50%" }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{modal.name}</div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Parent contact (from student profile)</div>
            </div>
          </div>

          {/* Quick Templates — premium row (spec §3):
              header + ⚫ ADD (+) + ⚫ SEE ALL (⋯) buttons; first 4
              chips inline; overflow chip; and two modals below for
              browsing / creating templates. Persistence uses
              localStorage['msgTemplates']. */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 10,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 600, color: "#94a3b8",
                letterSpacing: "0.6px", textTransform: "uppercase",
              }}>
                Quick Templates
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => { setTplLabel(""); setTplSubject(""); setTplBody(""); setShowAddTemplate(true); }}
                  title="Add custom template"
                  style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "#eff6ff", border: "1px solid #bfdbfe",
                    color: "#2563eb", cursor: "pointer", fontSize: 16,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700,
                  }}
                >+</button>
                <button
                  type="button"
                  onClick={() => setShowAllTemplates(true)}
                  title="All templates"
                  style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: "#f1f5f9", border: "1px solid #e2e8f0",
                    color: "#64748b", cursor: "pointer", fontSize: 16,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >⋯</button>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {allTemplates.slice(0, 4).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  style={{
                    background: "#f8fafc", border: "1px solid #e2e8f0",
                    borderRadius: 8, padding: "6px 12px",
                    fontSize: 12, cursor: "pointer", color: "#1b3a6b",
                    fontWeight: 500, display: "flex", alignItems: "center", gap: 5,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "#1b3a6b";
                    (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#1b3a6b";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "#f8fafc";
                    (e.currentTarget as HTMLButtonElement).style.color = "#1b3a6b";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                  }}
                >
                  <span>{t.icon}</span> {t.label}
                </button>
              ))}
              {allTemplates.length > 4 && (
                <button
                  type="button"
                  onClick={() => setShowAllTemplates(true)}
                  style={{
                    background: "none", border: "1px dashed #e2e8f0",
                    borderRadius: 8, padding: "6px 12px",
                    fontSize: 12, cursor: "pointer", color: "#94a3b8",
                  }}
                >
                  +{allTemplates.length - 4} more
                </button>
              )}
            </div>
          </div>

          {/* ALL TEMPLATES MODAL */}
          {showAllTemplates && (
            <div
              style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center",
              }}
              onClick={() => setShowAllTemplates(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff", borderRadius: 20, width: 480, maxHeight: "80vh",
                  overflow: "hidden", display: "flex", flexDirection: "column",
                  boxShadow: "0 25px 60px rgba(0,0,0,0.2)",
                }}
              >
                <div style={{
                  background: "#1b3a6b", padding: "18px 24px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>📋 All Templates</div>
                  <button
                    type="button"
                    onClick={() => setShowAllTemplates(false)}
                    style={{
                      background: "rgba(255,255,255,0.15)", border: "none",
                      color: "#fff", width: 28, height: 28, borderRadius: 6,
                      cursor: "pointer", fontSize: 16,
                    }}
                  >✕</button>
                </div>

                <div style={{ overflowY: "auto", padding: 16, flex: 1 }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: "#94a3b8",
                    letterSpacing: "0.8px", textTransform: "uppercase",
                    marginBottom: 8, marginLeft: 4,
                  }}>Default Templates</div>
                  {defaultTemplates.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => { applyTemplate(t); setShowAllTemplates(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "12px 14px", borderRadius: 10, marginBottom: 6,
                        border: "1px solid #e2e8f0", cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = "#f8fafc")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = "#fff")}
                    >
                      <span style={{ fontSize: 22 }}>{t.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                          {t.subject.replace("{student}", "Student")}
                        </div>
                      </div>
                      <span style={{ color: "#94a3b8", fontSize: 12 }}>Apply →</span>
                    </div>
                  ))}

                  {customTemplates.length > 0 && (
                    <>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: "#94a3b8",
                        letterSpacing: "0.8px", textTransform: "uppercase",
                        margin: "16px 0 8px 4px",
                      }}>My Templates</div>
                      {customTemplates.map((t, i) => (
                        <div
                          key={t.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "12px 14px", borderRadius: 10, marginBottom: 6,
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <span style={{ fontSize: 22 }}>{t.icon || "📝"}</span>
                          <div
                            style={{ flex: 1, cursor: "pointer" }}
                            onClick={() => { applyTemplate(t); setShowAllTemplates(false); }}
                          >
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{t.subject}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = customTemplates.filter((_, idx) => idx !== i);
                              persistCustom(updated);
                            }}
                            style={{
                              background: "#fef2f2", border: "none", color: "#dc2626",
                              width: 26, height: 26, borderRadius: 6, cursor: "pointer",
                              fontSize: 13,
                            }}
                          >🗑</button>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                <div style={{ padding: "14px 16px", borderTop: "1px solid #e2e8f0" }}>
                  <button
                    type="button"
                    onClick={() => { setShowAllTemplates(false); setTplLabel(""); setTplSubject(""); setTplBody(""); setShowAddTemplate(true); }}
                    style={{
                      width: "100%", background: "#1b3a6b", color: "#fff",
                      border: "none", borderRadius: 10, padding: "12px",
                      fontWeight: 700, fontSize: 13, cursor: "pointer",
                    }}
                  >
                    + Add New Template
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ADD TEMPLATE MODAL */}
          {showAddTemplate && (
            <div
              style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center",
              }}
              onClick={() => setShowAddTemplate(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff", borderRadius: 20, width: 460,
                  boxShadow: "0 25px 60px rgba(0,0,0,0.2)", overflow: "hidden",
                }}
              >
                <div style={{
                  background: "#1b3a6b", padding: "18px 24px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>+ New Template</div>
                  <button
                    type="button"
                    onClick={() => setShowAddTemplate(false)}
                    style={{
                      background: "rgba(255,255,255,0.15)", border: "none",
                      color: "#fff", width: 28, height: 28, borderRadius: 6,
                      cursor: "pointer", fontSize: 16,
                    }}
                  >✕</button>
                </div>

                <div style={{ padding: "20px 24px" }}>
                  <input
                    placeholder="Template name (e.g. Field Trip Notice)"
                    value={tplLabel}
                    onChange={(e) => setTplLabel(e.target.value)}
                    style={{
                      width: "100%", padding: "10px 12px", marginBottom: 10,
                      border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13,
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <input
                    placeholder="Email subject line"
                    value={tplSubject}
                    onChange={(e) => setTplSubject(e.target.value)}
                    style={{
                      width: "100%", padding: "10px 12px", marginBottom: 10,
                      border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13,
                      outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <textarea
                    placeholder="Message body... Use {student}, {parent}, {class}, {date} as placeholders"
                    value={tplBody}
                    onChange={(e) => setTplBody(e.target.value)}
                    style={{
                      width: "100%", padding: "10px 12px",
                      border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13,
                      minHeight: 100, resize: "vertical", outline: "none",
                      fontFamily: "Inter, sans-serif", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                    Tip: Use {"{student}"}, {"{parent}"}, {"{class}"}, {"{date}"}, {"{time}"} — they auto-fill when applied.
                  </div>
                </div>

                <div style={{
                  padding: "14px 24px", borderTop: "1px solid #e2e8f0",
                  display: "flex", gap: 10,
                }}>
                  <button
                    type="button"
                    onClick={() => setShowAddTemplate(false)}
                    style={{
                      flex: 1, background: "#f1f5f9", border: "none",
                      borderRadius: 9, padding: "11px", fontSize: 13,
                      fontWeight: 600, cursor: "pointer", color: "#64748b",
                    }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={() => {
                      const label = tplLabel.trim();
                      const subject = tplSubject.trim();
                      const body = tplBody.trim();
                      if (!label || !subject || !body) return;
                      const next = [...customTemplates, {
                        id: String(Date.now()), icon: "📝", label, subject, body,
                      }];
                      persistCustom(next);
                      setShowAddTemplate(false);
                    }}
                    style={{
                      flex: 2, background: "#1b3a6b", color: "#fff", border: "none",
                      borderRadius: 9, padding: "11px", fontSize: 13,
                      fontWeight: 700, cursor: "pointer",
                    }}
                  >💾 Save Template</button>
                </div>
              </div>
            </div>
          )}

          <input
            value={modal.subject}
            onChange={(e) => setModal({ ...modal, subject: e.target.value })}
            placeholder="Subject"
            style={{
              width: "100%", padding: "10px 12px", marginBottom: 10,
              border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13,
              outline: "none", boxSizing: "border-box",
            }}
          />
          <textarea
            value={modal.body}
            onChange={(e) => setModal({ ...modal, body: e.target.value })}
            placeholder="Write your message..."
            style={{
              width: "100%", padding: "10px 12px",
              border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13,
              minHeight: 120, resize: "vertical", outline: "none",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{
          padding: "14px 24px", background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between",
        }}>
          <button
            onClick={() => setModal(null)}
            style={{
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9,
              padding: "10px 20px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", color: "#64748b",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={loading}
            style={{
              background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 9,
              padding: "10px 24px", fontSize: 13, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 14px rgba(27,58,107,0.3)",
            }}
          >
            📧 Send Email →
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────── Attendance History Slide Panel ─────────────── //
const AttendanceHistoryPanel: React.FC<{
  student: { id: number; name: string } | null;
  onClose: () => void;
  navigate: (path: string) => void;
}> = ({ student, onClose, navigate }) => {
  const { data, isFetching } = useGetStudentAttendanceHistoryQuery(
    { studentId: student?.id ?? 0, limit: 20 },
    { skip: !student }
  );
  const payload: any = data?.data;
  const summary = payload?.summary || { present: 0, absent: 0, late: 0 };
  const records: any[] = payload?.records || [];

  const open = !!student;

  // ROOT CAUSE OF THE OLD CLIPPING BUG
  // ---------------------------------
  // MiniDrawer wraps every page in a MUI persistent-drawer main-content
  // element that applies `transform: translateX(...)` for the sidebar shift
  // animation. A child with `position: fixed` becomes fixed RELATIVE TO the
  // nearest transformed ancestor — not the viewport — so `right: 0` landed
  // ~240px inside the sidebar-shifted column, clipping the drawer's right
  // edge and the bottom "View Full Profile" button.
  //
  // Fix: portal the drawer + backdrop to document.body. Both are then true
  // viewport-fixed and land flush with the browser's right edge at every
  // width. `box-sizing: border-box` on the panel container + the footer
  // button prevents any width-plus-padding overflow.
  const drawer = (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1250,
          }}
        />
      )}
      <div
        style={{
          position: "fixed",
          right: open ? 0 : "-440px",
          top: 0, bottom: 0,
          width: "min(420px, 100vw)",
          maxWidth: "100vw",
          boxSizing: "border-box",
          background: "#fff", zIndex: 1300,
          boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
          transition: "right 0.3s ease",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{
          background: "#1b3a6b", padding: "18px 20px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>📋 Attendance History</div>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 }}>
              {student?.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
              width: 30, height: 30, borderRadius: 7, cursor: "pointer", fontSize: 18,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)",
          gap: 1, background: "#e2e8f0",
        }}>
          {[
            { label: "Present", value: summary.present, color: "#16a34a", bg: "#f0fdf4" },
            { label: "Absent",  value: summary.absent,  color: "#dc2626", bg: "#fef2f2" },
            { label: "Late",    value: summary.late,    color: "#d97706", bg: "#fffbeb" },
          ].map((s) => (
            <div key={s.label} style={{ background: s.bg, padding: "14px 0", textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{
                fontSize: 10, color: s.color, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.5px",
              }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {isFetching && (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
          )}
          {!isFetching && records.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              No attendance records yet.
            </div>
          )}
          {records.map((r) => {
            const color =
              r.status === "present" ? "#16a34a"
              : r.status === "absent"  ? "#dc2626"
              : r.status === "late"    ? "#d97706"
              : "#94a3b8";
            const bg =
              r.status === "present" ? "#dcfce7"
              : r.status === "absent"  ? "#fee2e2"
              : r.status === "late"    ? "#fef3c7"
              : "#f1f5f9";
            return (
              <div key={r.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderRadius: 10, marginBottom: 8,
                border: "1px solid #e2e8f0", background: "#fafafa",
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: color,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>
                    {r.class_name}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                    {r.date}{r.start_time ? ` · ${r.start_time}` : ""}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                  background: bg, color: color,
                }}>
                  {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "14px 16px", borderTop: "1px solid #e2e8f0", boxSizing: "border-box" }}>
          <button
            onClick={() => student && navigate(`/student-history/${student.id}`)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1b3a6b", color: "#fff",
              border: "none", borderRadius: 10, padding: 12,
              fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}
          >
            👤 View Full Profile →
          </button>
        </div>
      </div>
    </>
  );

  // Portal to document.body so no ancestor transform / overflow / stacking
  // context can clip or reposition the drawer.
  return typeof document !== "undefined"
    ? createPortal(drawer, document.body)
    : drawer;
};

export default AttendanceSession;
